import { Col, Row } from "react-bootstrap";
import { Typography } from "../../components/utilities/Typhograpy";
import { useContext, useEffect, useRef, useState } from "react";
import { GetListNotifications } from "../../api/NotificationApi";
import GlobalContext from "../../context/GlobalContext";
import BoxSkeleton from "../../components/utilities/BoxSkeleton";
import InboxDetailPage from "./InboxDetailPage";

import { tr } from "../../i18n";
const InboxPage = () => {
  const isMounted = useRef(true);
  const itemsPerPage = 8;
  const { user } = useContext(GlobalContext);
  const [NotificationLists, SetNotificationLists] = useState(null);
  const [selectedInbox, setSelectedInbox] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [counts, setCounts] = useState({ all: 0, unread: 0, approvals: 0 });
  const [emptyState, setEmptyState] = useState({
    title: tr("inbox:notification_list_is_empty"),
    message: tr("inbox:new_notifications_will_appear_here"),
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: itemsPerPage,
    total: 0,
    has_more: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isInboxLoading, setIsInboxLoading] = useState(true);

  const loadInbox = async ({
    page = 1,
    append = false,
    resetSelection = false,
    showSkeleton = false,
  } = {}) => {
    if (showSkeleton) {
      setIsFilterLoading(true);
    }

    if (!append) {
      setIsInboxLoading(true);
    }

    if (resetSelection) {
      setSelectedInbox(null);
    }

    try {
      const result = await GetListNotifications({
        user_id: user.user_id,
        company_id: user.workspace.company_id,
        filter: activeTab,
        search: debouncedSearchQuery,
        page,
        limit: itemsPerPage,
      });

      if (result.status == 200) {
        const responseData = result.data || {};
        const items = Array.isArray(responseData.items)
          ? responseData.items
          : [];

        SetNotificationLists((prev) =>
          append && Array.isArray(prev) ? [...prev, ...items] : items,
        );
        setCounts(responseData.counts || { all: 0, unread: 0, approvals: 0 });
        setEmptyState(
          responseData.empty_state || {
            title: tr("inbox:notification_list_is_empty"),
            message: tr("inbox:new_notifications_will_appear_here"),
          },
        );
        setPagination(
          responseData.pagination || {
            page,
            limit: itemsPerPage,
            total: items.length,
            has_more: false,
          },
        );
        setCurrentPage(page);
      } else {
        SetNotificationLists([]);
        setEmptyState({
          title: tr("inbox:notification_list_is_empty"),
          message: tr("inbox:new_notifications_will_appear_here"),
        });
      }
    } catch (error) {
      SetNotificationLists([]);
      setEmptyState({
        title: tr("inbox:notification_list_is_empty"),
        message: tr("inbox:new_notifications_will_appear_here"),
      });
    } finally {
      setIsInboxLoading(false);
      setIsFilterLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    loadInbox({
      page: 1,
      append: false,
      resetSelection: true,
      showSkeleton: !isInboxLoading,
    });
  }, [
    user?.user_id,
    user?.workspace?.company_id,
    activeTab,
    debouncedSearchQuery,
  ]);

  const isNotificationRead = (status) => {
    const normalized = String(status ?? "").toLowerCase();

    return (
      normalized === "1" ||
      normalized === "read" ||
      normalized === "true" ||
      normalized === "yes"
    );
  };

  const getPreview = (content) => {
    const text = String(content || "").trim();
    if (!text) {
      return "No additional details.";
    }

    return text.length > 88 ? `${text.slice(0, 88)}...` : text;
  };

  const hasMore = pagination.has_more;

  const handleSelectInbox = (notificationId) => {
    setSelectedInbox(notificationId);
    SetNotificationLists((prev) => {
      if (!Array.isArray(prev)) {
        return prev;
      }

      return prev.map((item) =>
        item.notification_id === notificationId
          ? { ...item, notification_is_read: 1 }
          : item,
      );
    });
  };

  return (
    <Row className="inbox-main-container g-3">
      <Col xs="12" lg="6">
        <div className="inbox-list-wrapper">
          <div className="inbox-header">
            <div className="inbox-header-content">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="inbox-header-icon"
              >
                <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0" />
              </svg>
              <div>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, color: "white" }}
                >
                  {tr("inbox:notifications")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    marginTop: "2px",
                  }}
                >
                  {counts.unread} {" "}{tr("inbox:notification")}{counts.unread !== 1 ? "s" : ""}
                </Typography>
              </div>
            </div>
          </div>
          <div className="inbox-search-container">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="inbox-search-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder={tr("inbox:search_notifications")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="inbox-search-input"
            />
          </div>
          <div className="inbox-tabs">
            <button
              type="button"
              className={`inbox-tab ${activeTab === "all" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("all");
              }}
            >
              {tr("inbox:all")}{counts.all || 0})
            </button>
            <button
              type="button"
              className={`inbox-tab ${activeTab === "unread" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("unread");
              }}
            >
              {tr("inbox:unread")}{counts.unread || 0})
            </button>
            <button
              type="button"
              className={`inbox-tab ${activeTab === "approvals" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("approvals");
              }}
            >
              {tr("inbox:approvals")}{counts.approvals || 0})
            </button>
          </div>
          <div className="inbox-lists">
            {isInboxLoading ? (
              <div className="container">
                <BoxSkeleton count="8" size="12" height="30px"></BoxSkeleton>
              </div>
            ) : NotificationLists ? (
              NotificationLists.length == 0 && !isFilterLoading ? (
                <div className="inbox-empty-state">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="inbox-empty-icon"
                  >
                    <polygon points="22 3 2 3 2 3 13 13.46 22 4 22 3" />
                    <polyline points="22 3 13 13.46 2 21 22 21" />
                  </svg>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: 500,
                      color: "#7f8c8d",
                      marginTop: "12px",
                    }}
                  >
                    {emptyState.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ color: "#bdc3c7", marginTop: "4px" }}
                  >
                    {emptyState.message}
                  </Typography>
                </div>
              ) : isFilterLoading ? (
                <BoxSkeleton count="8" size="12" height="30px"></BoxSkeleton>
              ) : (
                <>
                  {NotificationLists.map((data, index) => (
                    <div
                      className={`inbox-item ${
                        isNotificationRead(data.notification_is_read)
                          ? "inbox-item-read"
                          : "inbox-item-unread"
                      } ${
                        selectedInbox === data.notification_id
                          ? "inbox-item-selected"
                          : ""
                      }`}
                      key={index}
                      onClick={() => {
                        handleSelectInbox(data.notification_id);
                      }}
                    >
                      <div className="inbox-item-icon-wrapper">
                        <div className="inbox-item-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#4f8ef7"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"></path>
                          </svg>
                        </div>
                      </div>
                      <div className="inbox-item-content">
                        <div className="inbox-item-subject">
                          <Typography
                            style={{ fontWeight: 600, color: "#2c3e50" }}
                            variant="body2"
                          >
                            {data.notification_subject}
                          </Typography>
                        </div>
                        <div className="inbox-item-description">
                          <Typography
                            variant="body3"
                            style={{ color: "#7f8c8d" }}
                          >
                            {getPreview(data.notification_content)}
                          </Typography>
                        </div>
                      </div>
                    </div>
                  ))}
                  {hasMore && (
                    <div className="inbox-load-more-container">
                      <button
                        className="inbox-load-more-btn"
                        onClick={() =>
                          loadInbox({
                            page: currentPage + 1,
                            append: true,
                            resetSelection: false,
                            showSkeleton: false,
                          })
                        }
                      >
                        {tr("inbox:load_more")}
                      </button>
                    </div>
                  )}
                </>
              )
            ) : (
              <BoxSkeleton count="10" size="12" height="30px"></BoxSkeleton>
            )}
          </div>
        </div>
      </Col>
      <Col xs="12" lg="6">
        <InboxDetailPage idInbox={selectedInbox} />
      </Col>
    </Row>
  );
};

export default InboxPage;
